import { TraceService } from '../services/trace.service';
import { TraceRouteData } from './trace-route-data';
import hash from 'object-hash';

/**
 * dataRef : reference id; when the id complies to regex  ^traceRouteData[0-maxItems]?$ , then this id points to a local storage object with the same key name and the value contains all the data of one route; in all other cases the id is a permalink token that points to route data on our remote server
 * time : last date when this route was opened
 * remoteSource : true if the data is from a gpx import or a server permalink (on edit, this data is copied to a new history item with a false value for this property)
 * name : optional, if the route has a name, then this name will be copied to this field
 * hasDesc: optional, if the route has a descrtiption, then this field will be added here with value true (this boolean is only used in the import tab, during the check if the drawing should be shown in the list)
 * thumb : optional, preview image of the route
 * dist: optional, the total distance of the route 
 * upwardDist: optional, the total upwards distance of the route 
 * downwardDist: optional, the total downwards distance of the route 
 * appId: optional, used together with dataRef when it's a remote server token
 * hashCode : optional, hashcode of the route data, only added during file imports; this is used to compare different file imports because we don't want to keep doubles
 */
export type TraceRouteHistoryItemType = {
     dataRef: string;
     time: number;
     remoteSource: boolean;
     name?: string;
     hasDesc?: boolean,
     thumb?: string;
     dist?: number;
     upwardDist?: number;
     downwardDist?: number;
     appId?: number | string;
     hashCode?: string;
};

export class TraceRouteHistory {
     static readonly dataRefPrefix = 'routedata-';
     static readonly maxItems = 7;

     constructor(
          public historyItems: TraceRouteHistoryItemType[]
     ) {
          historyItems = TraceRouteData.getNativeClass(historyItems) === 'array' ? historyItems : [];
     }

     static isArrayOfTraceRouteHistoryItemType(
          historyItems: any
     ): historyItems is TraceRouteHistoryItemType[] {

          let isValid = TraceRouteData.getNativeClass(historyItems) === 'array';

          if (isValid) {
               historyItems.every((historyItem: any) => {
                    if (!TraceRouteHistory.isTraceRouteHistoryItemType(historyItem)) {
                         isValid = false;
                         return false;
                    }
                    return true;
               });
          }

          return isValid;
     }
     static isTraceRouteHistoryItemType(historyItem: any): historyItem is TraceRouteHistoryItemType {
          return (
               TraceRouteData.getNativeClass(historyItem) === 'object' &&
               typeof historyItem.dataRef === 'string' &&
               historyItem.dataRef.length > 0 &&
               (historyItem.dataRef.indexOf(TraceRouteHistory.dataRefPrefix) !== 0 || TraceRouteHistory.isLocalStorageDataReference(historyItem.dataRef)) &&
               typeof historyItem.time === 'number' &&
               historyItem.time > 0 &&
               typeof historyItem.remoteSource === 'boolean' &&
               (!historyItem.hasOwnProperty('name') || typeof historyItem.name === 'string') &&
               (!historyItem.hasOwnProperty('thumb') || typeof historyItem.thumb === 'string') &&
               (!historyItem.hasOwnProperty('dist') || (typeof historyItem.dist === 'number' && historyItem.dist > -1)) &&
               (!historyItem.hasOwnProperty('upwardDist') || (typeof historyItem.upwardDist === 'number' && historyItem.upwardDist > -1)) &&
               (!historyItem.hasOwnProperty('downwarddDist') || (typeof historyItem.downwardDist === 'number' && historyItem.upwardDist < 1)) &&
               (!historyItem.hasOwnProperty('appId') || (typeof historyItem.appId === 'number' && historyItem.appId > -1) || (typeof historyItem.appId === 'string' && historyItem.appId.trim().length > 0)) &&
               (!historyItem.hasOwnProperty('hashCode') || typeof historyItem.hashCode === 'string')
          );
     }

     static isLocalStorageDataReference(dataRef: string): boolean {
          const reg = new RegExp("^" + TraceRouteHistory.dataRefPrefix + "[0-9]{13,}$");
          return reg.test(dataRef);
     }

     cleanupHistoryItems(isQuickCleanup: boolean, dataRefToSkip?: string) {
          dataRefToSkip = typeof dataRefToSkip === 'string' ? dataRefToSkip : null;

          while (this.historyItems.length > TraceRouteHistory.maxItems) {
               const dataRef = this.historyItems[0].dataRef;

               if (dataRefToSkip && dataRef === dataRefToSkip) {
                    continue;
               }

               //this cleans up the list and the linked data object
               if (!this.removeHistoryItem(dataRef)) {
                    break;
               }
          }

          if (!isQuickCleanup) {
               //in the list with local ref, but no data object in storage or empty object in storage?
               for (let i = 0; i < this.historyItems.length; i++) {
                    if (dataRefToSkip && this.historyItems[i].dataRef === dataRefToSkip) {
                         continue;
                    }
                    if (TraceRouteHistory.isLocalStorageDataReference(this.historyItems[i].dataRef)) {
                         try {
                              const tempLocaleStorageString = window.localStorage.getItem(this.historyItems[i].dataRef);
                              const tempTraceRouteData = tempLocaleStorageString ? JSON.parse(tempLocaleStorageString) : {};

                              if (!TraceRouteData.isTraceRouteData(tempTraceRouteData) ||
                                   (tempTraceRouteData.wayPoints.length === 0
                                        && tempTraceRouteData.routes.length === 0
                                        && (!tempTraceRouteData.metadata.name || tempTraceRouteData.metadata.name.length === 0)
                                        && (!tempTraceRouteData.metadata.desc || tempTraceRouteData.metadata.desc.length === 0))) {
                                   this.historyItems.splice(i, 1);
                                   i--;
                              }
                         } catch (error) {
                         }
                    }
               }

               //data object in storage, but not in the list anymore?
               //if a shutdown happens during this procedure(which empties historyItems), we don't want the storage to be deleted by accident
               if (this.historyItems.length > 0) {
                    for (let i = 0; i < window.localStorage.length; i++) {
                         if (TraceRouteHistory.isLocalStorageDataReference(localStorage.key(i))) {
                              if (dataRefToSkip && localStorage.key(i) === dataRefToSkip) {
                                   continue;
                              }

                              if (this.findDataRef(localStorage.key(i)) === -1) {
                                   this._removeHistoryItemLinkedData(localStorage.key(i));
                              }
                         }
                    }
               }
          }
     }

     removeHistoryItem(dataRef: string): boolean {
          let position = this.findDataRef(dataRef);
          if (position > -1) {
               this.historyItems.splice(position, 1);
               this._removeHistoryItemLinkedData(dataRef);
          }
          return position > -1;
     }
     private _removeHistoryItemLinkedData(dataRef: string): boolean {
          if (TraceRouteHistory.isLocalStorageDataReference(dataRef)) {
               try {
                    if (window.localStorage && window.localStorage.getItem(dataRef)) {
                         window.localStorage.removeItem(dataRef);
                         return true;
                    }
               } catch (error) {
                    console.log('we were unable to cleanup localstorage; we couldn\'t remove the data with key name ' + dataRef);
               }
          }
          return false;
     }

     findDataRef(dataRef: string): number {
          let position = -1;
          let count = 0;
          this.historyItems.every(historyItem => {
               if (historyItem.dataRef === dataRef) {
                    position = count;
               }
               count++;
               return position === -1;
          });
          return position;
     }

     hasHistoryItems(): boolean {
          return this.historyItems.length > 0;
     }

     getMostRecentHistoryItem(): TraceRouteHistoryItemType {
          return this.hasHistoryItems() ? this.historyItems[this.historyItems.length - 1] : null;
     }

     updateMostRecentHistoryItem_baseInfo(traceRouteData: TraceRouteData, routeDistance: number): boolean {
          //time property is updated automatically
          const mostRecentHistoryItem = this.getMostRecentHistoryItem();
          if (mostRecentHistoryItem && traceRouteData) {

               if (mostRecentHistoryItem.remoteSource && TraceRouteHistory.isLocalStorageDataReference(mostRecentHistoryItem.dataRef)) {
                    //making sure temp drawId's are removed everywhere 
                    const traceRouteDataForHashCalculation = new TraceRouteData(
                         traceRouteData.epsgCode,
                         TraceRouteData.getFilteredTraceRouteDataObject(traceRouteData.metadata, true),
                         TraceRouteData.getFilteredTraceRouteDataArray(traceRouteData.wayPoints, true),
                         TraceRouteData.getFilteredTraceRouteDataArray(traceRouteData.routes, true),
                         TraceRouteData.getFilteredTraceRouteDataObject(traceRouteData.ttSettings, true));

                    mostRecentHistoryItem.hashCode = hash(traceRouteDataForHashCalculation, { algorithm: 'md5' });
               }

               if (traceRouteData.metadata.name && traceRouteData.metadata.name.length > 0) {
                    mostRecentHistoryItem.name = traceRouteData.metadata.name;
               } else {
                    if (mostRecentHistoryItem.name) {
                         delete mostRecentHistoryItem.name;
                    }
               }

               if (traceRouteData.metadata.desc && traceRouteData.metadata.desc.length > 0) {
                    mostRecentHistoryItem.hasDesc = true;
               } else {
                    if (mostRecentHistoryItem.hasDesc) {
                         delete mostRecentHistoryItem.hasDesc;
                    }
               }

               mostRecentHistoryItem.time = new Date().getTime();

               if (typeof routeDistance === "number" && routeDistance > -1) {
                    mostRecentHistoryItem.dist = routeDistance;
               } else if (typeof mostRecentHistoryItem.dist === "number") {
                    delete mostRecentHistoryItem.dist;
               }

               return true;
          }
          return false;
     }

     updateMostRecentHistoryItem_upDownDistance(routeUpwardDistance: number, routeDownwardDistance: number): boolean {
          const mostRecentHistoryItem = this.getMostRecentHistoryItem();
          if (mostRecentHistoryItem) {
               if (typeof routeUpwardDistance === "number" && routeUpwardDistance > -1) {
                    mostRecentHistoryItem.upwardDist = routeUpwardDistance;
               } else if (typeof mostRecentHistoryItem.upwardDist === "number") {
                    delete mostRecentHistoryItem.upwardDist;
               }
               if (typeof routeDownwardDistance === "number" && routeDownwardDistance < 1) {
                    mostRecentHistoryItem.downwardDist = routeDownwardDistance;
               } else if (typeof mostRecentHistoryItem.downwardDist === "number") {
                    delete mostRecentHistoryItem.downwardDist;
               }

               return true;
          }
          return false;
     }

     updateMostRecentHistoryItem_thumb(thumb: string): boolean {
          const mostRecentHistoryItem = this.getMostRecentHistoryItem();
          if (mostRecentHistoryItem) {
               if (typeof thumb === "string" && thumb.length > 0) {
                    mostRecentHistoryItem.thumb = thumb;
               } else if (typeof mostRecentHistoryItem.thumb === "string") {
                    delete mostRecentHistoryItem.thumb;
               }

               return true;
          }
          return false;
     }

     updateMostRecentHistoryItem_time(): boolean {
          const mostRecentHistoryItem = this.getMostRecentHistoryItem();
          if (mostRecentHistoryItem) {
               mostRecentHistoryItem.time = new Date().getTime();
               return true;
          }
          return false;
     }

     addNewHistoryItemOrMoveExistingToTop(isRemoteSource: boolean, permaLink?: string): TraceRouteHistoryItemType {
          let historyItem: TraceRouteHistoryItemType;
          if (permaLink && permaLink.trim().length > 0) {
               const position = this.findDataRef(permaLink);
               if (position > -1) {
                    //same permalink token was already in the list, move it to the most recent position
                    historyItem = this.historyItems.splice(position, 1)[0];
                    historyItem.remoteSource = isRemoteSource;
                    historyItem.time = new Date().getTime();
               } else {
                    historyItem = { dataRef: permaLink, time: new Date().getTime(), remoteSource: isRemoteSource };
               }
          } else {
               let exists: boolean;
               let creationTime = new Date().getTime();
               do {
                    exists = this.findDataRef(TraceRouteHistory.dataRefPrefix + creationTime) > -1;
                    creationTime += exists ? 1 : 0;
               } while (exists);
               historyItem = { dataRef: TraceRouteHistory.dataRefPrefix + creationTime, time: creationTime, remoteSource: isRemoteSource };
          }

          this.historyItems.push(historyItem);

          if (this.historyItems.length > TraceRouteHistory.maxItems) {
               this.cleanupHistoryItems(true);
          }

          return this.getMostRecentHistoryItem();
     }

     setAppIdForMostRecentHistoryItem(appId: number | string): boolean {
          const mostRecentHistoryItem = this.getMostRecentHistoryItem();
          if (mostRecentHistoryItem && ((typeof appId === 'number' && appId > -1) || (typeof appId === 'string' && appId.trim().length > 0))) {
               mostRecentHistoryItem.appId = appId;
               return true;
          }
          return false;
     }

     removeSameImportsFromHistory(hashCode: string) {
          let dataRef = null;
          let count = 0;
          this.historyItems.every(historyItem => {
               if (historyItem.remoteSource && typeof historyItem.hashCode === "string" && historyItem.hashCode === hashCode) {
                    dataRef = historyItem.dataRef;
               }
               count++;
               return dataRef === null;
          });

          if (dataRef) {
               this.removeHistoryItem(dataRef);
          }
     }

     isEditedHistoryItem(dataRef: string): boolean {
          let position = this.findDataRef(dataRef);
          if (position > -1) {
               return !this.historyItems[position].remoteSource
          }
          return false;
     }

     createUniqueName(oldName: string) {
          let newName = typeof oldName === "string" && oldName.trim().length > 0 ? oldName : "unnamed";
          let oldNameAlreadyWasCopy = false;
          if (/^.+\([0-9]+\)$/.test(newName)) {
               newName = newName.substring(0, newName.lastIndexOf("(")).trim();
               oldNameAlreadyWasCopy = true;
          }
          let count = oldNameAlreadyWasCopy ? 0 : -1;
          let foundName: boolean;
          do {
               foundName = false;
               const tempName = newName + (count > -1 ? " (" + (count + 2) + ")" : "");
               this.historyItems.every(historyItem => {
                    if (tempName === historyItem.name) {
                         foundName = true;
                         count++;
                         return false;
                    }
                    return true;
               });
          } while (foundName);
          newName += count > -1 ? " (" + (count + 2) + ")" : "";
          return newName;
     }
}